import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { formatRupiah, formatDateID } from "@/lib/format";
import {
  FileText, Archive, Radar, ShieldCheck, RefreshCw, PlusCircle, Search,
  AlertTriangle, CheckCircle2, XCircle, PlayCircle, ArrowRight, ClipboardCheck,
  Building2, FileSpreadsheet, Zap,
} from "lucide-react";

const TAX_TYPES = ["PPN", "PPh21", "PPh22", "PPh23", "PPh25", "PPhFinal"];
const SPT_STATUS_COLORS = {
  DRAF: "bg-slate-500", DIPERIKSA: "bg-sky-600", DISETUJUI: "bg-emerald-600",
  SIAP_DILAPORKAN: "bg-blue-600", DILAPORKAN: "bg-emerald-700",
  DIKUNCI: "bg-slate-700", DIBATALKAN: "bg-slate-400",
};
const INT_STATUS_COLORS = {
  SESUAI: "bg-emerald-600", PERLU_DITINJAU: "bg-amber-500",
  BLOCKER: "bg-rose-600", TIDAK_ADA_DATA: "bg-slate-400",
};

function Stat({ label, value, tone = "slate", sub, testid }) {
  const map = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    sky: "bg-sky-50 border-sky-200 text-sky-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    rose: "bg-rose-50 border-rose-200 text-rose-900",
    slate: "bg-slate-50 border-slate-200 text-slate-900",
    blue: "bg-blue-50 border-blue-200 text-blue-900",
  };
  return (
    <div className={`border rounded-md p-4 ${map[tone]}`} data-testid={testid}>
      <div className="text-xs opacity-80">{label}</div>
      <div className="font-mono-num text-lg font-semibold mt-1">{value}</div>
      {sub && <div className="text-xs opacity-70 mt-1">{sub}</div>}
    </div>
  );
}

function Crumb({ path }) {
  return <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">{path.map((p, i) => (<span key={i}>{p}{i < path.length - 1 && " › "}</span>))}</div>;
}

// ============ SPT WORKPAPER ============
export function SPTPage() {
  const [sp] = useSearchParams();
  const [items, setItems] = useState([]);
  const [year, setYear] = useState(sp.get("year") ? Number(sp.get("year")) : new Date().getFullYear());
  const [taxType, setTaxType] = useState(sp.get("tax_type") || "");
  const [monthFilter, setMonthFilter] = useState(sp.get("month") ? Number(sp.get("month")) : "");
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({
    tax_type: sp.get("tax_type") || "PPN",
    year: sp.get("year") ? Number(sp.get("year")) : new Date().getFullYear(),
    month: sp.get("month") ? Number(sp.get("month")) : new Date().getMonth() + 1,
  });
  const [selected, setSelected] = useState(null);
  const [drill, setDrill] = useState(null);
  const [recon, setRecon] = useState(null);
  const [overrideReason, setOverrideReason] = useState("");

  const load = async () => {
    try {
      const p = new URLSearchParams();
      if (year) p.set("year", year);
      if (taxType) p.set("tax_type", taxType);
      const { data } = await api.get(`/spt?${p.toString()}`);
      let rows = data || [];
      if (monthFilter) rows = rows.filter(r => r.month === monthFilter);
      setItems(rows);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, taxType, monthFilter]);

  const create = async () => {
    try {
      await api.post("/spt", form);
      toast.success(`Kertas Kerja SPT ${form.tax_type} ${form.year}-${String(form.month).padStart(2, "0")} dibuat`);
      setShowNew(false); await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openDetail = async (sid) => {
    try {
      const [d, dr, rc] = await Promise.all([
        api.get(`/spt/${sid}`), api.get(`/spt/${sid}/drilldown`), api.get(`/spt/${sid}/reconciliation`),
      ]);
      setSelected(d.data); setDrill(dr.data); setRecon(rc.data); setOverrideReason("");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doAction = async (action, extra = {}) => {
    if (!selected) return;
    try {
      await api.post(`/spt/${selected.id}/status`, { action, ...extra });
      toast.success(`SPT ${action}`);
      await openDetail(selected.id); await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.blocked === "RECONCILIATION_MISMATCH") {
        toast.error("Rekonsiliasi menunjukkan selisih. Berikan alasan override untuk melanjutkan.");
        setRecon(detail.recon);
      } else {
        toast.error(formatApiError(e));
      }
    }
  };

  return (
    <div className="space-y-6 page-fade" data-testid="spt-page">
      <div>
        <Crumb path={["Perpajakan", "Kertas Kerja SPT"]} />
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Kertas Kerja SPT</h1>
            <p className="text-slate-500 mt-1 text-sm">Persiapan SPT Masa PPN, PPh 21, 22, 23, 25, Final. Data dari transaksi dan Tax Engine — bukan input ulang.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={load} data-testid="spt-refresh"><RefreshCw className="h-4 w-4 mr-2" />Muat Ulang</Button>
            <Button onClick={() => setShowNew(true)} data-testid="spt-new-btn"><PlusCircle className="h-4 w-4 mr-2" />Buat Kertas Kerja</Button>
          </div>
        </div>
      </div>

      <div className="flex gap-3 items-end flex-wrap">
        <div><Label>Tahun</Label><Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className="w-28" data-testid="spt-year" /></div>
        <div><Label>Bulan</Label>
          <Select value={monthFilter === "" ? "ALL" : String(monthFilter)} onValueChange={(v) => setMonthFilter(v === "ALL" ? "" : Number(v))}>
            <SelectTrigger className="w-28" data-testid="spt-month"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Semua</SelectItem>
              {Array.from({length:12}).map((_,i) => <SelectItem key={i+1} value={String(i+1)}>{String(i+1).padStart(2,"0")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div><Label>Jenis Pajak</Label>
          <Select value={taxType || "ALL"} onValueChange={(v) => setTaxType(v === "ALL" ? "" : v)}>
            <SelectTrigger className="w-48" data-testid="spt-taxtype"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Semua</SelectItem>
              {TAX_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>ID</TableHead><TableHead>Jenis</TableHead><TableHead>Masa</TableHead>
            <TableHead>Terutang</TableHead><TableHead>Dibayar</TableHead><TableHead>K/L Bayar</TableHead>
            <TableHead>Trx</TableHead><TableHead>Dok</TableHead><TableHead>Status</TableHead><TableHead>Aksi</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={10} className="text-center text-slate-400 py-8">Belum ada kertas kerja.</TableCell></TableRow>}
            {items.map(it => (
              <TableRow key={it.id} data-testid={`spt-row-${it.id}`}>
                <TableCell className="font-mono text-xs">{it.id}</TableCell>
                <TableCell>{it.tax_type}</TableCell>
                <TableCell>{it.year}-{String(it.month).padStart(2, "0")}</TableCell>
                <TableCell className="font-mono-num">{it.snapshot?.has_data ? formatRupiah(it.snapshot?.tax_terutang) : <span className="text-slate-400">TIDAK ADA DATA</span>}</TableCell>
                <TableCell className="font-mono-num">{it.snapshot?.has_data ? formatRupiah(it.snapshot?.tax_dibayar) : "—"}</TableCell>
                <TableCell className="font-mono-num">{it.snapshot?.has_data ? formatRupiah(it.snapshot?.kurang_lebih_bayar) : "—"}</TableCell>
                <TableCell>{it.snapshot?.count_txn ?? 0}</TableCell>
                <TableCell>{it.snapshot?.count_docs ?? 0}</TableCell>
                <TableCell><Badge className={SPT_STATUS_COLORS[it.status] || "bg-slate-500"}>{it.status}</Badge></TableCell>
                <TableCell><Button size="sm" variant="outline" onClick={() => openDetail(it.id)} data-testid={`spt-detail-${it.id}`}>Detail</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      {showNew && (
        <Card><CardContent className="p-4 space-y-3">
          <h2 className="font-semibold">Buat Kertas Kerja SPT</h2>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Jenis</Label>
              <Select value={form.tax_type} onValueChange={(v) => setForm({ ...form, tax_type: v })}>
                <SelectTrigger data-testid="spt-form-taxtype"><SelectValue /></SelectTrigger>
                <SelectContent>{TAX_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Tahun</Label><Input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: Number(e.target.value) })} data-testid="spt-form-year" /></div>
            <div><Label>Bulan</Label><Input type="number" min={1} max={12} value={form.month} onChange={(e) => setForm({ ...form, month: Number(e.target.value) })} data-testid="spt-form-month" /></div>
          </div>
          <div className="flex gap-2">
            <Button onClick={create} data-testid="spt-form-submit">Buat</Button>
            <Button variant="outline" onClick={() => setShowNew(false)}>Batal</Button>
          </div>
        </CardContent></Card>
      )}

      {selected && (
        <Card><CardContent className="p-4 space-y-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h2 className="font-semibold text-lg">{selected.id}</h2>
              <div className="text-sm text-slate-500">{selected.tax_type} · Masa {selected.year}-{String(selected.month).padStart(2, "0")}</div>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={SPT_STATUS_COLORS[selected.status]}>{selected.status}</Badge>
              <Button size="sm" variant="ghost" onClick={() => { setSelected(null); setDrill(null); setRecon(null); }}>Tutup</Button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {selected.snapshot?.has_data ? (
              <>
                <Stat label="DPP" value={formatRupiah(selected.snapshot.dpp)} testid="spt-stat-dpp" />
                <Stat label="Pajak Terutang" value={formatRupiah(selected.snapshot.tax_terutang)} tone="sky" testid="spt-stat-terutang" />
                <Stat label="Pajak Dibayar" value={formatRupiah(selected.snapshot.tax_dibayar)} tone="emerald" testid="spt-stat-dibayar" />
                <Stat label="Kurang/Lebih Bayar" value={formatRupiah(selected.snapshot.kurang_lebih_bayar)} tone={selected.snapshot.kurang_lebih_bayar > 0 ? "amber" : "slate"} testid="spt-stat-klb" />
              </>
            ) : (
              <div className="col-span-4 p-6 bg-slate-50 rounded border text-center text-slate-500" data-testid="spt-no-data">
                TIDAK ADA DATA — {selected.tax_type} masa ini belum memiliki transaksi.
              </div>
            )}
          </div>

          {recon && (
            <div className="border rounded p-3 bg-slate-50">
              <div className="flex items-center gap-2 mb-2">
                <ClipboardCheck className="h-4 w-4" />
                <span className="font-semibold text-sm">Rekonsiliasi:</span>
                <Badge className={INT_STATUS_COLORS[recon.overall]}>{recon.overall}</Badge>
              </div>
              <ul className="text-sm space-y-1">
                {(recon.checks || []).map((c, i) => (
                  <li key={i} className="flex items-center gap-2" data-testid={`spt-recon-check-${i}`}>
                    {c.status === "SESUAI" ? <CheckCircle2 className="h-3 w-3 text-emerald-600" /> : <AlertTriangle className="h-3 w-3 text-amber-500" />}
                    <span className="flex-1">{c.name}</span>
                    <span className="font-mono text-xs">{formatRupiah(c.difference)}</span>
                  </li>
                ))}
              </ul>
              {recon.overall === "PERLU_DITINJAU" && (
                <div className="mt-3 space-y-2">
                  <Label className="text-xs">Alasan Override (wajib untuk lolos rekonsiliasi):</Label>
                  <Input value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} placeholder="Alasan tinjauan/koreksi" data-testid="spt-override-reason" />
                </div>
              )}
            </div>
          )}

          <div className="flex gap-2 flex-wrap">
            {selected.status === "DRAF" && <Button size="sm" onClick={() => doAction("PERIKSA")} data-testid="spt-action-periksa"><ArrowRight className="h-3 w-3 mr-1" />Periksa</Button>}
            {selected.status === "DIPERIKSA" && <Button size="sm" onClick={() => doAction("SETUJUI")} data-testid="spt-action-setujui"><CheckCircle2 className="h-3 w-3 mr-1" />Setujui</Button>}
            {selected.status === "DISETUJUI" && <Button size="sm" onClick={() => doAction("SIAPKAN", overrideReason ? { override_reason: overrideReason } : {})} data-testid="spt-action-siapkan">Siapkan untuk Dilaporkan</Button>}
            {selected.status === "SIAP_DILAPORKAN" && <Button size="sm" onClick={() => doAction("LAPORKAN")} data-testid="spt-action-laporkan">Laporkan</Button>}
            {selected.status === "DILAPORKAN" && <Button size="sm" variant="secondary" onClick={() => doAction("KUNCI")} data-testid="spt-action-kunci">Kunci</Button>}
            {!["DILAPORKAN", "DIKUNCI", "DIBATALKAN"].includes(selected.status) && <Button size="sm" variant="destructive" onClick={() => doAction("BATAL")} data-testid="spt-action-batal">Batalkan</Button>}
          </div>

          <Tabs defaultValue="drill">
            <TabsList><TabsTrigger value="drill">Drill-down Sumber</TabsTrigger><TabsTrigger value="history">Riwayat</TabsTrigger></TabsList>
            <TabsContent value="drill" className="space-y-2">
              {drill?.fakturs?.length > 0 && (
                <div><div className="text-xs font-semibold mb-1">Faktur ({drill.fakturs.length})</div>
                  <Table><TableHeader><TableRow><TableHead>Nomor</TableHead><TableHead>Tipe</TableHead><TableHead>Lawan</TableHead><TableHead>DPP</TableHead><TableHead>PPN</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                    <TableBody>{drill.fakturs.slice(0, 20).map(f => (
                      <TableRow key={f.id}><TableCell className="font-mono text-xs">{f.faktur_number}</TableCell><TableCell>{f.faktur_type}</TableCell><TableCell>{f.counterparty_name}</TableCell><TableCell className="font-mono-num">{formatRupiah(f.dpp)}</TableCell><TableCell className="font-mono-num">{formatRupiah(f.ppn)}</TableCell><TableCell><Badge className="bg-slate-500 text-xs">{f.status}</Badge></TableCell></TableRow>
                    ))}</TableBody></Table>
                </div>
              )}
              {drill?.transactions?.length > 0 && (
                <div><div className="text-xs font-semibold mb-1">Transaksi PPh ({drill.transactions.length})</div>
                  <Table><TableHeader><TableRow><TableHead>Tanggal</TableHead><TableHead>Referensi</TableHead><TableHead>Lawan</TableHead><TableHead>DPP</TableHead><TableHead>Pajak</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                    <TableBody>{drill.transactions.slice(0, 20).map(t => (
                      <TableRow key={t.id || t.year + t.month}><TableCell>{formatDateID(t.date || `${t.year}-${String(t.month).padStart(2, "0")}-01`)}</TableCell><TableCell className="font-mono text-xs">{t.reference_number || t.assessment_basis}</TableCell><TableCell>{t.counterparty_name || "-"}</TableCell><TableCell className="font-mono-num">{formatRupiah(t.dpp || t.amount)}</TableCell><TableCell className="font-mono-num">{formatRupiah(t.tax_amount || t.amount)}</TableCell><TableCell><Badge className="bg-slate-500 text-xs">{t.status || "-"}</Badge></TableCell></TableRow>
                    ))}</TableBody></Table>
                </div>
              )}
              {drill?.payments?.length > 0 && (
                <div><div className="text-xs font-semibold mb-1">Pembayaran ({drill.payments.length})</div>
                  <Table><TableHeader><TableRow><TableHead>Nomor</TableHead><TableHead>Tanggal</TableHead><TableHead>NTPN</TableHead><TableHead>Jumlah</TableHead></TableRow></TableHeader>
                    <TableBody>{drill.payments.map(p => (
                      <TableRow key={p.id}><TableCell className="font-mono text-xs">{p.number}</TableCell><TableCell>{formatDateID(p.payment_date)}</TableCell><TableCell className="font-mono text-xs">{p.ntpn || "-"}</TableCell><TableCell className="font-mono-num">{formatRupiah(p.amount)}</TableCell></TableRow>
                    ))}</TableBody></Table>
                </div>
              )}
              {(!drill?.fakturs?.length && !drill?.transactions?.length && !drill?.payments?.length) && <div className="text-sm text-slate-400 py-4 text-center">Tidak ada dokumen sumber.</div>}
            </TabsContent>
            <TabsContent value="history">
              <ul className="text-sm space-y-1">
                {(selected.workflow_history || []).map((h, i) => (
                  <li key={i} className="text-xs border-l-2 border-slate-200 pl-2">
                    <span className="text-slate-500">{formatDateID(h.at)}</span> · <b>{h.action}</b> {h.from && <>: {h.from} → {h.to}</>} · <span className="text-slate-500">{h.by}</span>
                    {h.note && <div className="text-slate-600 italic">{h.note}</div>}
                    {h.override_reason && <div className="text-amber-700">Override: {h.override_reason}</div>}
                  </li>
                ))}
              </ul>
            </TabsContent>
          </Tabs>
        </CardContent></Card>
      )}
    </div>
  );
}


// ============ ARSIP DOKUMEN PAJAK ============
export function ArsipPage() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_type: {} });
  const [filter, setFilter] = useState({ doc_type: "", year: "", month: "", npwp: "", q: "" });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      Object.entries(filter).forEach(([k, v]) => { if (v) p.set(k, v); });
      const [d, s] = await Promise.all([api.get(`/arsip?${p.toString()}`), api.get("/arsip/stats/summary")]);
      setItems(d.data || []); setStats(s.data || { total: 0, by_type: {} });
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const refresh = async () => {
    try { await api.post("/arsip/refresh"); toast.success("Arsip disinkronkan"); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6 page-fade" data-testid="arsip-page">
      <div>
        <Crumb path={["Perpajakan", "Arsip Dokumen Pajak"]} />
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Arsip Dokumen Pajak</h1>
            <p className="text-slate-500 mt-1 text-sm">Registry dokumen pajak: Faktur, Bukti Potong, Bukti Setor, NTPN, SPT. Auto-tersinkron dari transaksi sumber.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={refresh} data-testid="arsip-refresh"><RefreshCw className="h-4 w-4 mr-2" />Sinkronkan</Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Total Dokumen" value={stats.total} tone="blue" testid="arsip-stat-total" />
        <Stat label="Faktur Keluaran" value={stats.by_type?.FAKTUR_KELUARAN || 0} tone="emerald" testid="arsip-stat-fk" />
        <Stat label="Faktur Masukan" value={stats.by_type?.FAKTUR_MASUKAN || 0} tone="sky" testid="arsip-stat-fm" />
        <Stat label="Bukti Potong" value={stats.by_type?.BUKTI_POTONG || 0} tone="amber" testid="arsip-stat-bp" />
        <Stat label="Bukti Setor" value={stats.by_type?.BUKTI_SETOR || 0} tone="slate" testid="arsip-stat-bs" />
        <Stat label="NTPN" value={stats.by_type?.NTPN || 0} tone="slate" testid="arsip-stat-ntpn" />
        <Stat label="SPT" value={stats.by_type?.SPT || 0} tone="slate" testid="arsip-stat-spt" />
        <Stat label="Faktur Pengganti" value={stats.by_type?.FAKTUR_PENGGANTI || 0} tone="amber" testid="arsip-stat-fp" />
      </div>

      <Card><CardContent className="p-4 space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div><Label>Jenis</Label>
            <Select value={filter.doc_type || "ALL"} onValueChange={(v) => setFilter({ ...filter, doc_type: v === "ALL" ? "" : v })}>
              <SelectTrigger data-testid="arsip-filter-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Semua</SelectItem>
                <SelectItem value="FAKTUR_KELUARAN">Faktur Keluaran</SelectItem>
                <SelectItem value="FAKTUR_MASUKAN">Faktur Masukan</SelectItem>
                <SelectItem value="FAKTUR_PENGGANTI">Faktur Pengganti</SelectItem>
                <SelectItem value="BUKTI_POTONG">Bukti Potong</SelectItem>
                <SelectItem value="BUKTI_SETOR">Bukti Setor</SelectItem>
                <SelectItem value="NTPN">NTPN</SelectItem>
                <SelectItem value="SPT">SPT</SelectItem>
                <SelectItem value="PENDUKUNG">Pendukung</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Tahun</Label><Input type="number" value={filter.year} onChange={(e) => setFilter({ ...filter, year: e.target.value })} data-testid="arsip-filter-year" /></div>
          <div><Label>Bulan</Label><Input type="number" min={1} max={12} value={filter.month} onChange={(e) => setFilter({ ...filter, month: e.target.value })} data-testid="arsip-filter-month" /></div>
          <div><Label>NPWP</Label><Input value={filter.npwp} onChange={(e) => setFilter({ ...filter, npwp: e.target.value })} placeholder="NPWP lawan" data-testid="arsip-filter-npwp" /></div>
          <div><Label>Cari</Label><Input value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })} placeholder="Nomor/nama" data-testid="arsip-filter-q" /></div>
        </div>
        <Button onClick={load} disabled={loading} data-testid="arsip-search"><Search className="h-4 w-4 mr-2" />Cari</Button>
      </CardContent></Card>

      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Jenis</TableHead><TableHead>Nomor</TableHead><TableHead>Lawan Transaksi</TableHead>
            <TableHead>Masa</TableHead><TableHead>DPP</TableHead><TableHead>Pajak</TableHead>
            <TableHead>Status</TableHead><TableHead>Sumber</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">TIDAK ADA DATA</TableCell></TableRow>}
            {items.map(d => (
              <TableRow key={d.id} data-testid={`arsip-row-${d.id}`}>
                <TableCell><Badge className="bg-slate-600 text-xs">{d.doc_type}</Badge></TableCell>
                <TableCell className="font-mono text-xs">{d.doc_number}</TableCell>
                <TableCell>{d.counterparty_name || "-"}<div className="text-xs text-slate-400">{d.counterparty_npwp || ""}</div></TableCell>
                <TableCell>{d.period_year && d.period_month ? `${d.period_year}-${String(d.period_month).padStart(2, "0")}` : "-"}</TableCell>
                <TableCell className="font-mono-num">{formatRupiah(d.dpp || 0)}</TableCell>
                <TableCell className="font-mono-num">{formatRupiah(d.tax_amount || 0)}</TableCell>
                <TableCell><Badge className="bg-slate-500 text-xs">{d.status || "-"}</Badge></TableCell>
                <TableCell>{d.auto_generated ? <span className="text-xs text-emerald-700">AUTO</span> : <span className="text-xs text-blue-700">MANUAL</span>}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
      <div className="text-xs text-slate-500">Dokumen yang terkait masa pajak DILAPORKAN/DIKUNCI tidak dapat dihapus (retention lock).</div>
    </div>
  );
}


// ============ CORETAX CENTER ============
export function CoretaxCenterPage() {
  const [coretaxStatus, setCoretaxStatus] = useState(null);
  const [complianceData, setComplianceData] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [simulating, setSimulating] = useState(false);
  const [simResult, setSimResult] = useState(null);
  const [selectedScenario, setSelectedScenario] = useState("BERHASIL");
  const [sandboxLog, setSandboxLog] = useState([]);
  const [dashboardCards, setDashboardCards] = useState({});
  const [ruleFilter, setRuleFilter] = useState("ALL");
  const [levelFilter, setLevelFilter] = useState("ALL");
  const [ovrTarget, setOvrTarget] = useState(null);
  const [ovrReason, setOvrReason] = useState("");

  const load = async () => {
    try {
      const [ct, sc, cw, log, faktur, bp, pph, tp, spt] = await Promise.all([
        api.get("/tax/coretax/status"),
        api.get("/coretax5c/sandbox/scenarios"),
        api.get("/coretax5c/compliance"),
        api.get("/coretax5c/sandbox/log?limit=20"),
        api.get("/tax/faktur"),
        api.get("/pph/bukti-potong"),
        api.get("/pph/liability"),
        api.get("/pph/payment"),
        api.get("/spt"),
      ]);
      setCoretaxStatus(ct.data);
      setScenarios(sc.data?.scenarios || []);
      setComplianceData(cw.data);
      setSandboxLog(log.data?.entries || []);
      const fak = faktur.data || [];
      setDashboardCards({
        faktur_siap: fak.filter(f => ["VALID", "SIAP_DIKIRIM"].includes(f.status)).length,
        faktur_ditinjau: fak.filter(f => f.status === "DITOLAK" || f.original_faktur_id).length,
        bukti_potong: (bp.data || []).length,
        liabilities: (pph.data || []).filter(l => (l.closing_liability || 0) > 0).length,
        payments: (tp.data || []).length,
        spt_belum: (spt.data || []).filter(s => s.status !== "DILAPORKAN" && s.status !== "DIKUNCI").length,
        dokumen_masalah: (cw.data?.blockers || []).length,
        npwp_tidak_lengkap: (cw.data?.blockers || []).filter(b => b.code === "NPWP_KOSONG").length + (cw.data?.warnings || []).filter(b => b.code === "MASTER_DATA_TIDAK_LENGKAP").length,
        near_due: (pph.data || []).filter(l => l.status === "SEGERA_JATUH_TEMPO").length,
      });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const simulate = async () => {
    setSimulating(true); setSimResult(null);
    try {
      const { data } = await api.post("/coretax5c/sandbox/simulate", { scenario: selectedScenario });
      setSimResult(data); toast.success("Simulasi dijalankan");
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSimulating(false); }
  };

  const setCoretaxMode = async (mode) => {
    try {
      await api.put("/tax/coretax/mode", { mode });
      toast.success(`Mode Coretax: ${mode}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitOverride = async () => {
    if (!ovrTarget) return;
    if (!ovrReason || ovrReason.length < 5) { toast.error("Alasan override wajib diisi minimal 5 karakter."); return; }
    try {
      await api.post("/coretax5c/compliance/override", {
        entity_type: ovrTarget.entity_type || "compliance_rule",
        entity_id: ovrTarget.entity_id || ovrTarget.code || "generic",
        scope: ovrTarget.code || "OVERRIDE",
        reason: ovrReason,
      });
      toast.success("Override tercatat dengan audit trail.");
      setOvrTarget(null); setOvrReason("");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const coretaxApiConfigured = coretaxStatus?.api_configured === true;
  const showBelumTerhubung = coretaxStatus?.mode === "API" && !coretaxApiConfigured;

  return (
    <div className="space-y-6 page-fade" data-testid="coretax-center-page">
      <div>
        <Crumb path={["Perpajakan", "Coretax Center"]} />
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Coretax Center</h1>
            <p className="text-slate-500 mt-1 text-sm">Pusat operasional perpajakan: mode Coretax, ringkasan operasional, sandbox internal.</p>
          </div>
          <Button variant="outline" onClick={load} data-testid="coretax-refresh"><RefreshCw className="h-4 w-4 mr-2" />Muat Ulang</Button>
        </div>
      </div>

      {/* Status Bar */}
      <div className={`border rounded-md p-4 flex items-center gap-3 ${showBelumTerhubung ? "bg-amber-50 border-amber-300" : "bg-emerald-50 border-emerald-200"}`} data-testid="coretax-status-bar">
        <Radar className={`h-6 w-6 ${showBelumTerhubung ? "text-amber-600" : "text-emerald-600"}`} />
        <div className="flex-1">
          <div className="font-semibold text-sm">Mode: {coretaxStatus?.mode || "-"}</div>
          <div className="text-xs text-slate-600">{coretaxStatus?.message}</div>
        </div>
        {showBelumTerhubung && <Badge className="bg-amber-600" data-testid="coretax-belum-terhubung">API CORETAX: BELUM TERHUBUNG</Badge>}
        <div className="flex gap-2">
          {["MANUAL", "IMPORT_EXPORT", "API"].map(m => (
            <Button key={m} size="sm" variant={coretaxStatus?.mode === m ? "default" : "outline"} onClick={() => setCoretaxMode(m)} data-testid={`coretax-mode-${m}`}>{m}</Button>
          ))}
        </div>
      </div>

      {/* Dashboard Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Faktur Siap Diproses" value={dashboardCards.faktur_siap ?? 0} tone="emerald" testid="dash-faktur-siap" />
        <Stat label="Faktur Perlu Ditinjau" value={dashboardCards.faktur_ditinjau ?? 0} tone="amber" testid="dash-faktur-ditinjau" />
        <Stat label="Bukti Potong" value={dashboardCards.bukti_potong ?? 0} tone="sky" testid="dash-bukti-potong" />
        <Stat label="Kewajiban Pajak Aktif" value={dashboardCards.liabilities ?? 0} tone="slate" testid="dash-liabilities" />
        <Stat label="Pembayaran Pajak" value={dashboardCards.payments ?? 0} tone="slate" testid="dash-payments" />
        <Stat label="SPT Belum Dilaporkan" value={dashboardCards.spt_belum ?? 0} tone="amber" testid="dash-spt-belum" />
        <Stat label="Dokumen Bermasalah" value={dashboardCards.dokumen_masalah ?? 0} tone="rose" testid="dash-dokumen-masalah" />
        <Stat label="NPWP/NIK Tidak Lengkap" value={dashboardCards.npwp_tidak_lengkap ?? 0} tone="rose" testid="dash-npwp-tidak-lengkap" />
      </div>

      <Tabs defaultValue="compliance">
        <TabsList>
          <TabsTrigger value="compliance" data-testid="tab-compliance">Compliance Wall</TabsTrigger>
          <TabsTrigger value="sandbox" data-testid="tab-sandbox">Sandbox Simulator</TabsTrigger>
          <TabsTrigger value="log" data-testid="tab-log">Riwayat Simulasi</TabsTrigger>
        </TabsList>

        <TabsContent value="compliance">
          {complianceData && (() => {
            const allIssues = [
              ...(complianceData.blockers || []).map(x => ({ ...x, level: "BLOCKER" })),
              ...(complianceData.warnings || []).map(x => ({ ...x, level: "WARNING" })),
              ...(complianceData.infos || []).map(x => ({ ...x, level: "INFO" })),
            ];
            const rules = Array.from(new Set(allIssues.map(x => x.code))).sort();
            const filtered = allIssues.filter(x =>
              (ruleFilter === "ALL" || x.code === ruleFilter) &&
              (levelFilter === "ALL" || x.level === levelFilter)
            );
            return (
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <Stat label="Blocker" value={complianceData.summary?.blocker || 0} tone="rose" testid="comp-blocker" />
                  <Stat label="Warning" value={complianceData.summary?.warning || 0} tone="amber" testid="comp-warning" />
                  <Stat label="Info" value={complianceData.summary?.info || 0} tone="slate" testid="comp-info" />
                </div>
                <div className="flex gap-2 items-end flex-wrap">
                  <div><Label>Filter Rule</Label>
                    <Select value={ruleFilter} onValueChange={setRuleFilter}>
                      <SelectTrigger className="w-64" data-testid="comp-filter-rule"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ALL">Semua Rule</SelectItem>
                        {rules.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Filter Level</Label>
                    <Select value={levelFilter} onValueChange={setLevelFilter}>
                      <SelectTrigger className="w-40" data-testid="comp-filter-level"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ALL">Semua Level</SelectItem>
                        <SelectItem value="BLOCKER">Blocker</SelectItem>
                        <SelectItem value="WARNING">Warning</SelectItem>
                        <SelectItem value="INFO">Info</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="text-xs text-slate-500 mb-2">Menampilkan {filtered.length} dari {allIssues.length} issue</div>
                </div>
                {filtered.length === 0 && <div className="text-center text-slate-400 py-6">TIDAK ADA ISSUE SESUAI FILTER</div>}
                {filtered.map((it, i) => {
                  const styles = it.level === "BLOCKER"
                    ? "border-rose-300 bg-rose-50"
                    : it.level === "WARNING"
                      ? "border-amber-300 bg-amber-50"
                      : "border-slate-300 bg-slate-50";
                  const Icon = it.level === "BLOCKER" ? XCircle : it.level === "WARNING" ? AlertTriangle : FileText;
                  const iconCls = it.level === "BLOCKER" ? "text-rose-600" : it.level === "WARNING" ? "text-amber-600" : "text-slate-500";
                  return (
                    <div key={`f-${i}`} className={`border rounded p-3 flex items-start gap-2 ${styles}`} data-testid={`comp-item-${i}`}>
                      <Icon className={`h-4 w-4 mt-0.5 ${iconCls}`} />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold">{it.code}</span>
                          <Badge className={it.level === "BLOCKER" ? "bg-rose-600" : it.level === "WARNING" ? "bg-amber-500" : "bg-slate-500"}>{it.level}</Badge>
                        </div>
                        <div className="text-sm">{it.message}</div>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => setOvrTarget({ ...it, entity_id: it.entity_id || it.reference || it.code })} data-testid={`comp-override-${i}`}>Override</Button>
                    </div>
                  );
                })}
                {ovrTarget && (
                  <Card className="border-2 border-amber-400 bg-amber-50">
                    <CardContent className="p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="text-sm"><b>Override: {ovrTarget.code}</b> — {ovrTarget.message}</div>
                        <Button size="sm" variant="ghost" onClick={() => { setOvrTarget(null); setOvrReason(""); }}>Batal</Button>
                      </div>
                      <Label className="text-xs">Alasan Override (minimal 5 karakter, tersimpan di audit trail):</Label>
                      <Input value={ovrReason} onChange={(e) => setOvrReason(e.target.value)} placeholder="Alasan bisnis..." data-testid="comp-override-reason" />
                      <Button size="sm" onClick={submitOverride} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="comp-override-submit"><CheckCircle2 className="h-3 w-3 mr-1" />Simpan Override</Button>
                    </CardContent>
                  </Card>
                )}
              </div>
            );
          })()}
        </TabsContent>

        <TabsContent value="sandbox">
          <Card><CardContent className="p-4 space-y-3">
            <div className="border-l-4 border-amber-500 bg-amber-50 p-3 rounded">
              <div className="text-sm font-bold text-amber-800" data-testid="sandbox-label">SIMULASI INTERNAL — BUKAN SISTEM DJP</div>
              <div className="text-xs text-amber-700 mt-1">Simulator ini hanya untuk demo dan pelatihan. Respons tidak berasal dari sistem DJP resmi.</div>
            </div>
            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <Label>Skenario</Label>
                <Select value={selectedScenario} onValueChange={setSelectedScenario}>
                  <SelectTrigger data-testid="sandbox-scenario"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {scenarios.map(s => <SelectItem key={s.code} value={Object.keys({BERHASIL:1,DITOLAK:1,DATA_TIDAK_VALID:1,DUPLIKAT:1,NPWP_TIDAK_VALID:1,MASA_DIKUNCI:1}).find(k => scenarios.find(x=>x.code===s.code).code === s.code) || s.code}>{s.code} — {s.message}</SelectItem>)}
                    <SelectItem value="BERHASIL">BERHASIL</SelectItem>
                    <SelectItem value="DITOLAK">DITOLAK</SelectItem>
                    <SelectItem value="DATA_TIDAK_VALID">DATA TIDAK VALID</SelectItem>
                    <SelectItem value="DUPLIKAT">DUPLIKAT</SelectItem>
                    <SelectItem value="NPWP_TIDAK_VALID">NPWP/NIK TIDAK VALID</SelectItem>
                    <SelectItem value="MASA_DIKUNCI">MASA PAJAK DIKUNCI</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={simulate} disabled={simulating} data-testid="sandbox-run"><PlayCircle className="h-4 w-4 mr-2" />Jalankan Simulasi</Button>
            </div>
            {simResult && (
              <div className="border rounded p-3 bg-slate-50" data-testid="sandbox-result">
                <div className="text-xs font-semibold text-amber-700">{simResult.label}</div>
                <div className="mt-2 text-sm"><b>Skenario:</b> {simResult.scenario}</div>
                <div className="text-sm"><b>Kode:</b> <span className="font-mono">{simResult.response_code}</span></div>
                <div className="text-sm"><b>Pesan:</b> {simResult.message}</div>
                <div className="text-xs text-slate-500 mt-1">Simulasi dijalankan oleh {simResult.simulated_by} pada {formatDateID(simResult.simulated_at)}</div>
              </div>
            )}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="log">
          <div className="text-xs text-amber-700 mb-2">SIMULASI INTERNAL — BUKAN SISTEM DJP</div>
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead>Waktu</TableHead><TableHead>Skenario</TableHead><TableHead>Kode</TableHead><TableHead>Pesan</TableHead><TableHead>Oleh</TableHead></TableRow></TableHeader>
              <TableBody>
                {sandboxLog.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-6 text-slate-400">Belum ada simulasi.</TableCell></TableRow>}
                {sandboxLog.map((l, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-xs">{formatDateID(l.simulated_at)}</TableCell>
                    <TableCell><Badge className="bg-slate-500 text-xs">{l.scenario}</Badge></TableCell>
                    <TableCell className="font-mono text-xs">{l.response_code}</TableCell>
                    <TableCell className="text-sm">{l.message}</TableCell>
                    <TableCell className="text-xs">{l.simulated_by}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}


// ============ FINAL TAX INTEGRITY ============
export function FinalIntegrityPage() {
  const [data, setData] = useState(null);
  const load = async () => {
    try { const { data } = await api.get("/coretax5c/final-integrity"); setData(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const overall = data?.overall || "-";
  return (
    <div className="space-y-6 page-fade" data-testid="final-integrity-page">
      <div>
        <Crumb path={["Perpajakan", "Pusat Integritas Pajak"]} />
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Pusat Integritas Pajak</h1>
            <p className="text-slate-500 mt-1 text-sm">Rekonsiliasi 10 jalur: Invoice/Bill ↔ Faktur ↔ Subledger ↔ GL ↔ Payment ↔ SPT ↔ Arsip.</p>
          </div>
          <div className="flex gap-2 items-center">
            <Badge className={INT_STATUS_COLORS[overall] || "bg-slate-500"} data-testid="integrity-overall">{overall}</Badge>
            <Button variant="outline" onClick={load} data-testid="integrity-refresh"><RefreshCw className="h-4 w-4 mr-2" />Muat Ulang</Button>
          </div>
        </div>
      </div>

      <div className="grid gap-3">
        {data?.checks?.map((c, idx) => (
          <Card key={idx}><CardContent className="p-4" data-testid={`integrity-check-${idx}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm">{idx + 1}. {c.name}</span>
                  <Badge className={INT_STATUS_COLORS[c.status] || "bg-slate-500"}>{c.status}</Badge>
                </div>
                {c.checks && (
                  <div className="mt-2 space-y-1">
                    {c.checks.map((sub, si) => (
                      <div key={si} className="text-xs flex items-center gap-2 pl-3" data-testid={`integrity-sub-${idx}-${si}`}>
                        <span>›</span><span className="flex-1">{sub.name}</span>
                        {sub.status === "TIDAK_ADA_DATA" ? <span className="text-slate-400">TIDAK ADA DATA</span> : (
                          <>
                            <span className="font-mono">Δ {formatRupiah(sub.difference)}</span>
                            <Badge className={`${INT_STATUS_COLORS[sub.status]} text-xs`}>{sub.status}</Badge>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {!c.checks && c.expected !== undefined && (
                  <div className="mt-1 text-xs text-slate-600">
                    <span>Ekspektasi: <b className="font-mono">{formatRupiah(c.expected)}</b></span>
                    <span className="ml-3">Aktual: <b className="font-mono">{formatRupiah(c.actual)}</b></span>
                    <span className="ml-3">Selisih: <b className={`font-mono ${Math.abs(c.difference || 0) < 0.01 ? "text-emerald-700" : "text-amber-700"}`}>{formatRupiah(c.difference)}</b></span>
                  </div>
                )}
                {c.mismatch_count !== undefined && (
                  <div className="mt-1 text-xs text-slate-600">Mismatch: {c.mismatch_count} dari {c.spt_count || 0} SPT</div>
                )}
                {c.drilldown && <a href={c.drilldown} className="text-xs text-sky-600 hover:underline mt-1 inline-block" data-testid={`integrity-drill-${idx}`}>Drill-down →</a>}
              </div>
            </div>
          </CardContent></Card>
        ))}
      </div>

      {data && (
        <div className="text-xs text-slate-500 text-right">Digenerate pada {formatDateID(data.generated_at)}</div>
      )}
    </div>
  );
}
